import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Upload, ScanLine, Check, PackageX, Camera, X, Aperture } from "lucide-react";
import api from "../lib/api";
import { UNITS, CATEGORIES } from "../lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

export default function ReceiptDialog({ open, onOpenChange, onSaved }) {
  const [stage, setStage] = useState("choose"); // choose | camera | preview | scanning | review
  const [items, setItems] = useState([]);
  const [importing, setImporting] = useState(false);
  const [captured, setCaptured] = useState(null);
  const fileRef = useRef();
  const videoRef = useRef();
  const canvasRef = useRef();
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStream(null);
  }, []);

  const reset = useCallback(() => { stopCamera(); setStage("choose"); setItems([]); setCaptured(null); }, [stopCamera]);

  const scanImage = useCallback(async (dataUrl) => {
    setStage("scanning");
    try {
      const { data } = await api.post("/receipt/scan", { image_base64: dataUrl });
      const parsed = (data.items || []).map((it) => ({
        selected: true,
        name: it.name || "",
        price: it.price ?? "",
        package_qty: it.quantity ?? "",
        package_unit: "count",
        category: it.category || "Other",
        needs_package_size: !!it.needs_package_size,
      }));
      setItems(parsed);
      setStage("review");
      if (parsed.length === 0) toast.info("No items detected. Try a clearer photo.");
    } catch {
      toast.error("Could not read the receipt. Please try again.");
      setStage("choose");
    }
  }, []);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCaptured(reader.result); setStage("preview"); };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    setStage("camera");
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false,
      });
      streamRef.current = s;
      setStream(s); // triggers the attach effect regardless of mount order
    } catch (err) {
      toast.error(
        err?.name === "NotAllowedError"
          ? "Camera permission denied. Allow camera access or upload a file instead."
          : "No camera available. Please upload a file instead."
      );
      setStage("choose");
    }
  };

  // attach stream to the <video> whenever both the element and the stream exist
  useEffect(() => {
    if (stage === "camera" && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      const p = videoRef.current.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [stage, stream]);

  // cleanup camera on unmount / dialog close
  useEffect(() => () => stopCamera(), [stopCamera]);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) { toast.error("Camera not ready yet"); return; }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    stopCamera();
    setCaptured(dataUrl);
    setStage("preview");
  };

  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const importItems = async () => {
    const chosen = items.filter((it) => it.selected && it.name);
    if (chosen.length === 0) { toast.error("Select at least one item"); return; }
    setImporting(true);
    try {
      for (const it of chosen) {
        await api.post("/foods", {
          name: it.name, category: it.category,
          package_unit: it.package_unit,
          price: it.price ? parseFloat(it.price) : null,
          package_qty: it.package_qty ? parseFloat(it.package_qty) : null,
          needs_package_size: it.needs_package_size || !it.package_qty,
        });
      }
      toast.success(`${chosen.length} item(s) added to your library`);
      onOpenChange(false); reset(); onSaved && onSaved();
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[560px]" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Add From Receipt</DialogTitle></DialogHeader>

        {stage === "choose" && (
          <div className="grid grid-cols-1 gap-3 py-4 sm:grid-cols-2">
            <button onClick={startCamera} data-testid="receipt-camera-trigger"
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-brand/40 bg-brand-light py-10 text-brand transition-colors hover:border-brand">
              <Camera className="h-8 w-8" />
              <span className="font-semibold">Scan with camera</span>
              <span className="px-4 text-center text-xs text-brand/70">Use your phone, iPad or desktop webcam</span>
            </button>
            <button onClick={() => fileRef.current?.click()} data-testid="receipt-upload-trigger"
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-slate-200 py-10 text-slate-400 transition-colors hover:border-brand hover:text-brand">
              <Upload className="h-8 w-8" />
              <span className="font-semibold">Upload a photo</span>
              <span className="px-4 text-center text-xs">Choose an image from your device</span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
              className="hidden" data-testid="receipt-upload-input" />
          </div>
        )}

        {stage === "camera" && (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-2xl bg-black">
              <video ref={videoRef} autoPlay playsInline muted
                onLoadedMetadata={() => { const p = videoRef.current?.play(); if (p && p.catch) p.catch(() => {}); }}
                className="h-[340px] w-full object-cover" data-testid="receipt-camera-video" />
              <div className="pointer-events-none absolute inset-4 rounded-xl border-2 border-white/60" />
              {!stream && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-sm">Starting camera…</span>
                </div>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="receipt-camera-cancel"
                className="flex-1 rounded-full border-slate-200"><X className="mr-1.5 h-4 w-4" />Cancel</Button>
              <Button onClick={capturePhoto} data-testid="receipt-capture-button"
                className="flex-[2] rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
                <Aperture className="mr-1.5 h-5 w-5" />Capture receipt
              </Button>
            </div>
            <p className="text-center text-xs text-slate-400">Fill the frame with your receipt, then tap Capture</p>
          </div>
        )}

        {stage === "preview" && (
          <div className="space-y-3" data-testid="receipt-preview">
            <p className="text-sm text-slate-500">Make sure the receipt is sharp and readable before scanning.</p>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
              <img src={captured} alt="Captured receipt" className="max-h-[360px] w-full object-contain" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={startCamera} data-testid="receipt-retake-button"
                className="flex-1 rounded-full border-slate-200"><Camera className="mr-1.5 h-4 w-4" />Retake</Button>
              <Button onClick={() => scanImage(captured)} data-testid="receipt-scan-button"
                className="flex-[2] rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
                <ScanLine className="mr-1.5 h-5 w-5" />Scan receipt
              </Button>
            </div>
            <button onClick={() => fileRef.current?.click()} data-testid="receipt-choose-different"
              className="w-full text-center text-xs font-semibold text-slate-400 hover:text-brand">or choose a different photo</button>
          </div>
        )}

        {stage === "scanning" && (
          <div className="flex flex-col items-center gap-3 py-16 text-slate-500">
            <ScanLine className="h-10 w-10 animate-pulse text-brand" />
            <p className="font-semibold">Reading your receipt…</p>
            <Loader2 className="h-5 w-5 animate-spin text-brand" />
          </div>
        )}

        {stage === "review" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Review and approve before adding to your Food Library.</p>
            {items.map((it, i) => (
              <div key={i} data-testid={`receipt-item-${i}`}
                className="rounded-xl border border-slate-100 bg-white p-3">
                <div className="flex items-center gap-2">
                  <Checkbox checked={it.selected} onCheckedChange={(v) => setItem(i, "selected", !!v)}
                    data-testid={`receipt-item-check-${i}`} />
                  <Input value={it.name} onChange={(e) => setItem(i, "name", e.target.value)}
                    className="h-9 flex-1" placeholder="Item name" />
                </div>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  <Input value={it.price} onChange={(e) => setItem(i, "price", e.target.value)}
                    type="number" step="0.01" placeholder="Price" className="h-9" />
                  <Input value={it.package_qty} onChange={(e) => setItem(i, "package_qty", e.target.value)}
                    type="number" step="0.01" placeholder="Qty" className="h-9" />
                  <Select value={it.package_unit} onValueChange={(v) => setItem(i, "package_unit", v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={it.category} onValueChange={(v) => setItem(i, "category", v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {(it.needs_package_size || !it.package_qty) && (
                  <div className="mt-2 flex items-center gap-1 text-xs font-semibold text-amber-600">
                    <PackageX className="h-3.5 w-3.5" />Needs package size
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {stage === "review" && (
          <DialogFooter>
            <Button onClick={importItems} disabled={importing} data-testid="receipt-import-button"
              className="w-full rounded-full bg-brand py-6 text-base font-semibold hover:bg-brand-hover">
              {importing ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="mr-1.5 h-5 w-5" />Add selected to Library</>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
